import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

// Accepted upload formats. Matched by MIME type or, as a fallback, by file extension
// (some browsers report an empty/unreliable MIME type for TIFF).
const ALLOWED_MIME = ['application/pdf', 'image/tiff', 'image/jpeg', 'image/png'];
const ALLOWED_EXT = ['pdf', 'tif', 'tiff', 'jpg', 'jpeg', 'png'];
// Only these can be shown as an <img> thumbnail; others get a file badge.
const PREVIEWABLE_MIME = ['image/jpeg', 'image/png'];

/** @param {File} file */
function fileExtension(file) {
  const dot = file.name.lastIndexOf('.');
  return dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase();
}

/** @param {File} file */
function isAllowedFile(file) {
  return ALLOWED_MIME.includes(file.type) || ALLOWED_EXT.includes(fileExtension(file));
}

/**
 * <print-customizer>
 *
 * Drives the print product form: paper + size selection, custom-size live pricing,
 * multi-photo upload, and add-to-cart. Fixed sizes use the product's real variants
 * (quantity = number of photos). The "Custom" size uses a $0.01 helper variant where
 * quantity carries the computed price in cents (hidden/relabeled in the cart).
 */
class PrintCustomizer extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    // ---- Config from data attributes ----
    this.customLabel = this.dataset.customLabel || 'Custom';
    this.sizeUnit = this.dataset.sizeUnit || 'in';
    this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 10;
    this.maxFileBytes = (parseFloat(this.dataset.maxFileMb) || 20) * 1024 * 1024;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';
    // Longest short side the printer can hold. 0 disables the limit.
    this.maxShortSide = parseFloat(this.dataset.maxShortSide) || 0;
    // Smallest custom print the studio makes, compared side-for-side rather than by
    // area, so orientation doesn't matter: 10x8 passes, 9x9 doesn't. 0 disables.
    this.minShortSide = parseFloat(this.dataset.minShortSide) || 0;
    this.minLongSide = parseFloat(this.dataset.minLongSide) || 0;

    // ---- Add-ons ----
    // Absent data attributes mean the merchant picked no product, so the add-on
    // isn't offered and none of its code paths run.
    this.ccVariantId = this.dataset.ccVariant || null;
    this.ccPriceCents = parseInt(this.dataset.ccPrice, 10) || 0;
    this.rushVariantId = this.dataset.rushVariant || null;
    this.rushPriceCents = parseInt(this.dataset.rushPrice, 10) || 0;
    this.rushMessage = this.dataset.rushMessage || '';

    // ---- Variant data ----
    const variantScript = this.querySelector('[data-print-variants]');
    try {
      this.variants = JSON.parse(variantScript?.textContent || '[]');
    } catch (e) {
      this.variants = [];
    }
    this.variantMap = new Map();
    this.paperImages = new Map();
    this.paperBadges = new Map();
    // Papers that still have at least one size. A paper whose every size is hidden
    // has no variants here, and its tile is dropped rather than offered as a dead end.
    this.papersOffered = new Set();
    for (const v of this.variants) {
      this.variantMap.set(this.#key(v.paper, v.size), v);
      this.papersOffered.add(v.paper);
      if (v.image && !this.paperImages.has(v.paper)) {
        this.paperImages.set(v.paper, v.image);
      }
      if (v.badge && !this.paperBadges.has(v.paper)) {
        this.paperBadges.set(v.paper, v.badge);
      }
    }
    // The full price list drives custom pricing; `variants` above is already filtered
    // by the product's hidden_sizes and so would cut a paper's curve short.
    try {
      this.priceList = JSON.parse(this.querySelector('[data-print-price-list]')?.textContent || '[]');
    } catch (e) {
      this.priceList = [];
    }
    if (!this.priceList.length) this.priceList = this.variants;
    this.paperCurves = this.#buildPaperCurves();

    // ---- DOM refs ----
    this.form = this.querySelector('.print-customizer__form');
    this.customFields = this.querySelector('[data-custom-fields]');
    this.customHint = this.querySelector('[data-custom-hint]');
    this.fileInput = this.querySelector('[data-file-input]');
    this.photoHint = this.querySelector('[data-photo-hint]');
    this.orderEl = this.querySelector('[data-order]');
    this.orderList = this.querySelector('[data-order-list]');
    this.photoCountEl = this.querySelector('[data-photo-count]');
    this.totalPriceEl = this.querySelector('[data-total-price]');
    this.submitBtn = this.querySelector('[data-submit]');
    this.termsCheckbox = this.querySelector('[data-terms]');
    this.spinner = this.querySelector('.print-customizer__submit-spinner');
    this.errorEl = this.querySelector('[data-error]');
    this.successEl = this.querySelector('[data-success]');
    this.successTextEl = this.querySelector('[data-success-text]');
    this.addonsEl = this.querySelector('[data-addons]');
    this.ccToggle = this.querySelector('[data-cc-toggle]');
    this.ccStatus = this.querySelector('[data-cc-status]');
    this.ccRow = this.querySelector('[data-cc-row]');
    this.ccRowLabel = this.querySelector('[data-cc-row-label]');
    this.ccTotalEl = this.querySelector('[data-cc-total]');
    this.rushToggle = this.querySelector('[data-rush-toggle]');
    this.rushRow = this.querySelector('[data-rush-row]');
    this.rushTotalEl = this.querySelector('[data-rush-total]');
    this.productionNote = this.querySelector('[data-production-note]');
    // Captured before anything rewrites them, since both get swapped in place.
    this.productionMessage = this.productionNote?.textContent ?? '';
    this.ccLabel = this.ccRowLabel?.textContent ?? 'Color correction';

    // ---- State ----
    // Each photo captures the paper/size/price selected AT UPLOAD TIME, so
    // changing the selectors later never re-prices photos already added.
    // `colorCorrect` is per-photo and travels with it through grouping and submit.
    /** @type {{file: File, id: string, paper: string, size: string, isCustom: boolean, width: number|null, height: number|null, unitCents: number, variantId: number, colorCorrect: boolean}[]} */
    this.photos = [];

    // ---- Wire up ----
    this.form.addEventListener('submit', this.#onSubmit);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onChange);
    this.querySelector('[data-add-photos]')?.addEventListener('click', () => this.fileInput.click());
    this.fileInput.addEventListener('change', this.#onFilesPicked);
    this.ccToggle?.addEventListener('change', this.#onCcMasterToggle);

    this.#renderPaperTiles();
    this.#syncSizeTiles();
    this.#syncCustomVisibility();
    this.#recompute();
  }

  #key(paper, size) {
    return `${paper ?? ''}||${size ?? ''}`;
  }

  get selectedPaper() {
    return this.querySelector('input[name="print-paper"]:checked')?.value || null;
  }

  get selectedSize() {
    return this.querySelector('input[name="print-size"]:checked')?.value || null;
  }

  get isCustom() {
    return this.selectedSize === this.customLabel;
  }

  get dimensions() {
    const width = parseFloat(this.querySelector('[data-dim="width"]')?.value);
    const height = parseFloat(this.querySelector('[data-dim="height"]')?.value);
    return { width: width > 0 ? width : null, height: height > 0 ? height : null };
  }

  #money(cents) {
    return formatMoney(Math.round(cents), this.moneyFormat, this.currency);
  }

  /**
   * Parses a size option value ("16x20") into [width, height]. Null for anything
   * that isn't two numbers — notably the "Custom" value itself.
   */
  #parseSize(size) {
    const match = /^\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*$/i.exec(size ?? '');
    if (!match) return null;
    const width = Number(match[1]);
    const height = Number(match[2]);
    return width > 0 && height > 0 ? [width, height] : null;
  }

  #fitsPrinter(width, height) {
    return !this.maxShortSide || Math.min(width, height) <= this.maxShortSide;
  }

  /**
   * Whether a custom size clears the studio's smallest print. Standard sizes below it
   * are untouched — they're still sold as fixed variants, and they stay on the pricing
   * curve so a custom size just above the floor still prices off the sizes below it.
   */
  #meetsMinimum(width, height) {
    return (
      Math.min(width, height) >= this.minShortSide && Math.max(width, height) >= this.minLongSide
    );
  }

  /** The smallest custom size, written the way the hint shows it ("8 × 10 in"). */
  #minimumLabel() {
    return `${this.minShortSide} × ${this.minLongSide} ${this.sizeUnit}`;
  }

  /**
   * One price curve per paper — [area, price in cents] ascending by area — built from
   * that paper's own standard prices. Those prices are the curve, so a price edited in
   * admin reprices custom sizes too, with no second copy to keep in step.
   *
   * Sizes too wide for the printer are skipped. They're still sold as fixed variants,
   * but they can't be ordered as a custom size, and they aren't on the same volume
   * curve: a 48x48 costs more than the larger 40x60 because it's printed differently.
   * Leaving them in would make the curve dip, letting a bigger custom print quote less.
   */
  #buildPaperCurves() {
    const curves = new Map();
    for (const variant of this.priceList) {
      const dims = this.#parseSize(variant.size);
      if (!dims || !this.#fitsPrinter(dims[0], dims[1])) continue;
      let points = curves.get(variant.paper);
      if (!points) curves.set(variant.paper, (points = []));
      points.push([dims[0] * dims[1], variant.price]);
    }
    for (const points of curves.values()) points.sort((a, b) => a[0] - b[0]);
    return curves;
  }

  /**
   * Live price in cents for a custom-size print, interpolated from the selected paper's
   * own standard sizes. Because the curve is the paper's real prices, a custom size that
   * happens to match a standard one prices identically to it.
   *
   * Below the smallest standard size, that size's price applies — nothing is sold for
   * less than the smallest print. Above the largest, its rate per square unit applies
   * flat, which stops the volume discount rather than extrapolating it forever.
   *
   * Null when it can't be priced: missing dimensions, a short side the printer can't
   * hold, or a paper with no standard sizes to interpolate from.
   */
  #customUnitCents(paper, width, height) {
    if (!(width > 0) || !(height > 0)) return null;
    if (!this.#fitsPrinter(width, height)) return null;
    if (!this.#meetsMinimum(width, height)) return null;

    const points = this.paperCurves.get(paper);
    if (!points?.length) return null;

    const area = width * height;
    const [firstArea, firstPrice] = points[0];
    const [lastArea, lastPrice] = points[points.length - 1];
    if (area <= firstArea) return firstPrice;
    if (area >= lastArea) return area * (lastPrice / lastArea);

    for (let i = 1; i < points.length; i++) {
      const [a1, p1] = points[i];
      if (area <= a1) {
        const [a0, p0] = points[i - 1];
        return p0 + ((area - a0) / (a1 - a0)) * (p1 - p0);
      }
    }
    return null;
  }

  /** The custom-size hint line: the live price, or why there isn't one yet. */
  #customHintText() {
    const { width, height } = this.dimensions;
    if (!width || !height) return 'Enter width and height to see the price.';
    if (!this.#meetsMinimum(width, height)) {
      return `The smallest custom size is ${this.#minimumLabel()}. Please enter ${this.#minimumLabel()} or larger.`;
    }
    if (!this.#fitsPrinter(width, height)) {
      return `The shorter side must be ${this.maxShortSide} ${this.sizeUnit} or less.`;
    }
    const cents = this.#customUnitCents(this.selectedPaper, width, height);
    if (cents == null) return 'Enter width and height to see the price.';
    return `${width} × ${height} ${this.sizeUnit} · ${this.#money(cents)} per photo`;
  }

  #onChange = (event) => {
    // A new paper can offer a different set of sizes, and may force the selected
    // size to change, which in turn decides whether the custom fields belong.
    if (event.target?.name === 'print-paper') this.#syncSizeTiles();
    if (event.target?.name === 'print-size' || event.target?.name === 'print-paper') {
      this.#syncCustomVisibility();
    }
    this.#recompute();
  };

  /**
   * Shows only the sizes the selected paper is actually sold in. Sizes hidden for a
   * paper are absent from the variant JSON, so a missing map entry means "not offered"
   * — no separate list to keep in step.
   *
   * If the selected size isn't offered on a newly picked paper, selection moves to the
   * first size that is, so the form is never left in a state the customer can't submit
   * and can't see the cause of.
   */
  #syncSizeTiles() {
    const paper = this.selectedPaper;
    const inputs = Array.from(this.querySelectorAll('input[name="print-size"]'));
    if (!inputs.length) return;

    let firstOffered = null;
    let selectionSurvives = false;

    for (const input of inputs) {
      // With no paper chosen there is nothing to filter by, so show every size.
      const offered = !paper || this.variantMap.has(this.#key(paper, input.value));
      const tile = input.closest('.print-customizer__option');
      if (tile) tile.hidden = !offered;
      // Keep hidden radios out of keyboard traversal as well as out of sight.
      input.disabled = !offered;

      if (offered) {
        if (!firstOffered) firstOffered = input;
        if (input.checked) selectionSurvives = true;
      }
    }

    // Checking another radio in the group clears the stale one.
    if (!selectionSurvives && firstOffered) firstOffered.checked = true;
  }

  #syncCustomVisibility() {
    if (!this.customFields) return;
    this.customFields.hidden = !this.isCustom;
  }

  /** Populates each paper tile's image box and optional badge from the variant. */
  #renderPaperTiles() {
    let firstOfferedPaper = null;
    let paperSelectionSurvives = false;

    this.querySelectorAll('input[name="print-paper"]').forEach((input) => {
      const tile = input.closest('.print-customizer__option');

      // Every size hidden on this paper leaves nothing to sell, so drop the tile.
      const offered = this.papersOffered.has(input.value);
      if (tile) tile.hidden = !offered;
      input.disabled = !offered;
      if (offered) {
        if (!firstOfferedPaper) firstOfferedPaper = input;
        if (input.checked) paperSelectionSurvives = true;
      }

      const inner = tile?.querySelector('.print-customizer__option-inner');
      if (!inner) return;

      const media = inner.querySelector('[data-paper-media]');
      if (media) {
        const src = this.paperImages.get(input.value);
        if (src) {
          media.style.backgroundImage = `url("${src}")`;
          media.hidden = false;
        } else {
          media.hidden = true;
        }
      }

      const badgeText = this.paperBadges.get(input.value);
      let badge = inner.querySelector('.print-customizer__badge');
      if (badgeText) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'print-customizer__badge';
          inner.prepend(badge);
        }
        badge.textContent = badgeText;
      } else if (badge) {
        badge.remove();
      }
    });

    if (!paperSelectionSurvives && firstOfferedPaper) firstOfferedPaper.checked = true;
  }

  #onFilesPicked = (event) => {
    const picked = Array.from(event.target.files || []);
    // Photos join the CURRENTLY selected paper + size (+ dimensions) and lock
    // in that price now, so a later selection change never affects them.
    const unit = this.#currentUnit();
    if (!unit) {
      this.#flashHint(
        this.isCustom ? this.#customHintText() : 'Select a paper and size, then add photos.'
      );
      this.fileInput.value = '';
      return;
    }
    for (const file of picked) {
      if (this.photos.length >= this.maxFiles) {
        this.#flashHint(`You can add up to ${this.maxFiles} photos.`);
        break;
      }
      if (!isAllowedFile(file)) {
        this.#flashHint(`${file.name} is not a supported format (PDF, TIFF, JPG, PNG).`);
        continue;
      }
      if (file.size > this.maxFileBytes) {
        this.#flashHint(`${file.name} is larger than the allowed size.`);
        continue;
      }
      this.photos.push({
        file,
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        ...unit,
        // Never inherited. #syncAddons ticks the master checkbox as soon as any
        // one photo is marked, so reading it here meant marking a single image
        // silently color-corrected every image added afterwards — a per-image
        // charge nobody chose. The master toggle still marks all on demand.
        colorCorrect: false,
      });
    }
    // allow re-picking the same file again later
    this.fileInput.value = '';
    this.#renderOrder();
    this.#recompute();
  };

  /**
   * The master toggle is all-or-nothing: it marks or clears every photo. The
   * per-photo badges then refine that, and #syncAddons reflects a partial
   * selection back as the checkbox's indeterminate state.
   */
  #onCcMasterToggle = (event) => {
    const on = event.target.checked;
    for (const photo of this.photos) photo.colorCorrect = on;
    this.#renderOrder();
    // The delegated change handler recomputes right after this.
  };

  /** Photos marked for color correction, in the order they were added. */
  #ccPhotos() {
    return this.ccVariantId ? this.photos.filter((photo) => photo.colorCorrect) : [];
  }

  #flashHint(message) {
    if (!this.photoHint) return;
    const original = this.photoHint.dataset.original ?? this.photoHint.textContent;
    this.photoHint.dataset.original = original;
    this.photoHint.textContent = message;
    clearTimeout(this._hintTimeout);
    this._hintTimeout = setTimeout(() => {
      this.photoHint.textContent = this.photoHint.dataset.original;
    }, 4000);
  }

  #groupKey(item) {
    return item.isCustom
      ? `${item.paper}||${item.size}||${item.width}x${item.height}`
      : `${item.paper}||${item.size}`;
  }

  #groupLabel(item) {
    const size = item.isCustom ? `${item.width}×${item.height} ${this.sizeUnit}` : item.size;
    return `${item.paper} · ${size}`;
  }

  /** Groups the current photos by paper+size, preserving first-seen order. */
  #groupedPhotos() {
    const map = new Map();
    for (const p of this.photos) {
      const key = this.#groupKey(p);
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          paper: p.paper,
          size: p.size,
          isCustom: p.isCustom,
          width: p.width,
          height: p.height,
          unitCents: p.unitCents,
          variantId: p.variantId,
          label: this.#groupLabel(p),
          photos: [],
        };
        map.set(key, g);
      }
      g.photos.push(p);
    }
    return [...map.values()];
  }

  #photoThumb(photo) {
    const li = document.createElement('li');
    li.className = 'print-customizer__photo';

    const media = document.createElement('div');
    media.className = 'print-customizer__photo-media';

    if (PREVIEWABLE_MIME.includes(photo.file.type)) {
      const img = document.createElement('img');
      img.alt = photo.file.name;
      img.src = URL.createObjectURL(photo.file);
      img.addEventListener('load', () => URL.revokeObjectURL(img.src), { once: true });
      media.append(img);
    } else {
      const badge = document.createElement('span');
      badge.className = 'print-customizer__photo-badge';
      badge.textContent = (fileExtension(photo.file) || 'file').toUpperCase();
      badge.title = photo.file.name;
      media.append(badge);
    }

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'print-customizer__photo-remove';
    remove.setAttribute('aria-label', `Remove ${photo.file.name}`);
    remove.textContent = '×';
    remove.addEventListener('click', () => {
      this.photos = this.photos.filter((p) => p.id !== photo.id);
      this.#renderOrder();
      this.#recompute();
    });

    media.append(remove);
    li.append(media);

    if (this.ccVariantId) {
      const label = document.createElement('label');
      label.className = 'print-customizer__photo-cc';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.checked = !!photo.colorCorrect;
      input.setAttribute(
        'aria-label',
        `Color correction for ${photo.file.name} (${this.#money(this.ccPriceCents)})`
      );
      input.addEventListener('change', () => {
        photo.colorCorrect = input.checked;
        this.#renderOrder();
        this.#recompute();
      });

      const text = document.createElement('span');
      text.textContent = 'Color correction';

      label.append(input, text);
      li.append(label);
    }

    return li;
  }

  /** Renders the order grouped by paper + size, each with count and subtotal. */
  #renderOrder() {
    if (!this.orderList) return;
    this.orderList.innerHTML = '';
    const groups = this.#groupedPhotos();
    if (this.orderEl) this.orderEl.hidden = groups.length === 0;

    for (const g of groups) {
      const card = document.createElement('div');
      card.className = 'print-customizer__group-card';

      const head = document.createElement('div');
      head.className = 'print-customizer__group-head';

      const title = document.createElement('span');
      title.className = 'print-customizer__group-title';
      title.textContent = g.label;

      const sub = document.createElement('span');
      sub.className = 'print-customizer__group-sub';
      const count = g.photos.length;
      sub.textContent = `${count} × ${this.#money(g.unitCents)} = ${this.#money(g.unitCents * count)}`;

      head.append(title, sub);

      const list = document.createElement('ul');
      list.className = 'print-customizer__photos list-unstyled';
      for (const photo of g.photos) list.append(this.#photoThumb(photo));

      card.append(head, list);
      this.orderList.append(card);
    }

    // Under the images, not only in the summary further down: the per-image
    // charge belongs next to the checkboxes that create it, while the customer
    // is still deciding. Appears only once something is actually marked, so an
    // unmarked order never sees a price it isn't paying.
    const ccPhotos = this.#ccPhotos();
    if (ccPhotos.length) {
      const note = document.createElement('div');
      note.className = 'print-customizer__cc-note';

      note.textContent = `${this.ccLabel} — ${this.#money(this.ccPriceCents)} per image`;
      this.orderList.append(note);
    }
  }

  /**
   * Pricing snapshot for the CURRENT paper/size selection (one unit, no photos),
   * or null if the selection isn't complete/valid.
   * @returns {{paper: string, size: string, isCustom: boolean, width: number|null, height: number|null, unitCents: number, variantId: number} | null}
   */
  #currentUnit() {
    const paper = this.selectedPaper;
    const size = this.selectedSize;
    if (!paper || !size) return null;

    const variant = this.variantMap.get(this.#key(paper, size));
    if (!variant) return null;

    if (this.isCustom) {
      const { width, height } = this.dimensions;
      const unitCents = this.#customUnitCents(paper, width, height);
      if (unitCents == null) return null;
      return {
        paper,
        size,
        isCustom: true,
        width,
        height,
        unitCents: Math.max(1, Math.round(unitCents)),
        variantId: variant.id,
      };
    }

    return {
      paper,
      size,
      isCustom: false,
      width: null,
      height: null,
      unitCents: variant.price,
      variantId: variant.id,
    };
  }

  #recompute() {
    const photoCount = this.photos.length;
    this.photoCountEl.textContent = String(photoCount);

    // Custom-size hint. A size the studio won't print reads as a warning rather than
    // as the neutral price line it replaces.
    if (this.isCustom && this.customHint) {
      const { width, height } = this.dimensions;
      this.customHint.textContent = this.#customHintText();
      this.customHint.classList.toggle(
        'print-customizer__hint--warning',
        !!width && !!height && !this.#meetsMinimum(width, height)
      );
    }

    // Total = sum of every added photo's own locked-in price, plus add-ons.
    const printCents = this.photos.reduce((sum, p) => sum + p.unitCents, 0);
    const ccCount = this.#ccPhotos().length;
    const ccCents = ccCount * this.ccPriceCents;
    const rushOn = !!this.rushToggle?.checked;
    const rushCents = rushOn ? this.rushPriceCents : 0;

    this.#syncAddons({ photoCount, ccCount, ccCents, rushOn, rushCents });

    const totalCents = printCents + ccCents + rushCents;
    if (this.totalPriceEl) this.totalPriceEl.textContent = photoCount ? this.#money(totalCents) : '—';
    const termsOk = !this.termsCheckbox || this.termsCheckbox.checked;
    if (this.submitBtn) this.submitBtn.disabled = photoCount === 0 || !termsOk;
  }

  /**
   * Reflects add-on state into the UI. The block stays hidden until there are
   * photos to apply it to — a $2-per-image upsell over an empty form reads as a
   * price rise rather than an option.
   */
  #syncAddons({ photoCount, ccCount, ccCents, rushOn, rushCents }) {
    if (this.addonsEl) this.addonsEl.hidden = photoCount === 0;

    if (this.ccToggle) {
      // Partially-marked shows as indeterminate, so the checkbox never claims
      // every photo is being corrected when only some are.
      this.ccToggle.checked = ccCount > 0;
      this.ccToggle.indeterminate = ccCount > 0 && ccCount < photoCount;
    }
    if (this.ccStatus) {
      this.ccStatus.textContent =
        ccCount === 0
          ? 'No color correction selected.'
          : `${ccCount} of ${photoCount} ${photoCount === 1 ? 'image' : 'images'} · ${this.#money(ccCents)} — use the checkboxes below to change.`;
    }
    if (this.ccRow) {
      this.ccRow.hidden = ccCount === 0;
      if (this.ccRowLabel) this.ccRowLabel.textContent = `${this.ccLabel} × ${ccCount}`;
      if (this.ccTotalEl) this.ccTotalEl.textContent = this.#money(ccCents);
    }

    if (this.rushRow) {
      this.rushRow.hidden = !rushOn;
      if (this.rushTotalEl) this.rushTotalEl.textContent = this.#money(rushCents);
    }
    if (this.productionNote) {
      this.productionNote.textContent =
        rushOn && this.rushMessage ? this.rushMessage : this.productionMessage;
    }
  }

  #setLoading(loading) {
    if (this.spinner) this.spinner.hidden = !loading;
    if (loading) {
      this.submitBtn.disabled = true;
    } else {
      // Let the current selection decide whether the button is enabled.
      this.#recompute();
    }
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  #showSuccess(message) {
    if (!this.successEl) return;
    if (this.successTextEl) this.successTextEl.textContent = message;
    this.successEl.hidden = !message;
    clearTimeout(this._successTimeout);
    if (message) {
      this._successTimeout = setTimeout(() => {
        this.successEl.hidden = true;
      }, 6000);
    }
  }

  /**
   * Adds one add-on line. Mirrors the print path's multipart post so both go
   * through the same cart API semantics, minus the file uploads.
   */
  async #addAddon(variantId, quantity, properties) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(quantity));
    formData.append('properties[_addon]', '1');
    for (const [key, value] of Object.entries(properties)) {
      formData.append(`properties[${key}]`, value);
    }

    const cfg = fetchConfig('javascript', { body: formData });
    if (cfg.headers) delete cfg.headers['Content-Type'];

    const response = await fetch(Theme.routes.cart_add_url, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'application/json' },
    });
    const result = await response.json();
    if (result.status) {
      throw new Error(result.description || result.message || 'Could not add to cart.');
    }
  }

  /** True when the cart already carries this variant, so a flat fee isn't billed twice. */
  async #cartHasVariant(variantId) {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      return (cart.items || []).some((item) => String(item.variant_id) === String(variantId));
    } catch (error) {
      // A cart we couldn't read is not evidence the fee is absent. Skipping a fee
      // the customer owes is recoverable; charging rush twice is a refund.
      console.error('[print-customizer] could not check cart for an existing fee', error);
      return true;
    }
  }

  /** Filenames for the order admin, capped so a long list can't blow the property limit. */
  #fileNameList(photos) {
    const joined = photos.map((photo) => photo.file.name).join(', ');
    return joined.length <= 240 ? joined : `${photos.length} images — see the print lines above`;
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    this.#showError('');
    this.#showSuccess('');

    const groups = this.#groupedPhotos();
    if (groups.length === 0) {
      this.#showError('Add at least one photo to your order.');
      return;
    }

    if (this.termsCheckbox && !this.termsCheckbox.checked) {
      this.#showError('Please accept the Terms & Conditions before adding to cart.');
      return;
    }

    this.#setLoading(true);

    try {
      // Add each paper+size group as its own cart line item.
      for (const g of groups) {
        const count = g.photos.length;
        const formData = new FormData();
        formData.append('id', String(g.variantId));
        // Custom-size groups carry the computed price via quantity-in-cents on
        // the $0.01 helper variant; fixed sizes use the real per-print price.
        formData.append('quantity', String(g.isCustom ? g.unitCents * count : count));
        formData.append('properties[_print]', '1');
        if (this.termsCheckbox) formData.append('properties[Terms accepted]', 'Yes');
        formData.append('properties[Paper]', g.paper);
        formData.append('properties[Size]', g.isCustom ? this.customLabel : g.size);
        formData.append('properties[Photos]', String(count));
        if (g.isCustom) {
          formData.append('properties[Dimensions]', `${g.width} × ${g.height} ${this.sizeUnit}`);
          formData.append('properties[_custom]', '1');
          formData.append('properties[_unit_price]', this.#money(g.unitCents));
        }
        // The color-correction marker rides on the file's own property name, so the
        // order admin shows it on the same row as the download link rather than in
        // a filename list to cross-reference.
        g.photos.forEach((photo, index) => {
          const label = photo.colorCorrect
            ? `Artwork ${index + 1} (Color correction)`
            : `Artwork ${index + 1}`;
          formData.append(`properties[${label}]`, photo.file, photo.file.name);
        });

        const cfg = fetchConfig('javascript', { body: formData });
        // fetchConfig sets a JSON content-type; remove it so the browser sets
        // the correct multipart boundary for the file upload.
        if (cfg.headers) delete cfg.headers['Content-Type'];

        const response = await fetch(Theme.routes.cart_add_url, {
          ...cfg,
          headers: { ...cfg.headers, Accept: 'application/json' },
        });
        const result = await response.json();
        if (result.status) {
          throw new Error(result.description || result.message || 'Could not add to cart.');
        }
      }

      // Color correction bills per marked photo as its own line, so the charge is
      // real money in the cart rather than a note on the print line.
      const ccPhotos = this.#ccPhotos();
      if (ccPhotos.length) {
        await this.#addAddon(this.ccVariantId, ccPhotos.length, {
          'Color corrected': this.#fileNameList(ccPhotos),
        });
      }

      // Rush is a flat per-order fee. This form can be submitted repeatedly into
      // the same cart (another paper, a second batch), so it must not stack.
      if (this.rushVariantId && this.rushToggle?.checked) {
        if (!(await this.#cartHasVariant(this.rushVariantId))) {
          await this.#addAddon(this.rushVariantId, 1, {});
        }
      }

      // Record T&C acceptance at the order (cart) level for admin records.
      if (this.termsCheckbox) {
        try {
          await fetch(Theme.routes.cart_update_url || '/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: { 'Terms accepted': `Yes — ${new Date().toISOString()}` },
            }),
          });
        } catch (attrError) {
          console.error('[print-customizer] could not record terms acceptance', attrError);
        }
      }

      // Success: reset the whole order immediately so a later cart-refresh
      // error can't leave stale photos that inflate the next order.
      const addedCount = this.photos.length;
      this.photos = [];
      // The rush fee now lives in the cart; leaving the box ticked would quote it
      // again on the next batch and then correctly refuse to charge it.
      if (this.rushToggle) this.rushToggle.checked = false;
      this.#renderOrder();
      this.#recompute();
      this.#showSuccess(`Added ${addedCount} ${addedCount === 1 ? 'photo' : 'photos'} to your cart.`);

      // Best-effort cart UI refresh + drawer open (isolated).
      try {
        const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => undefined);
        document.dispatchEvent(
          new CartAddEvent(cart ?? {}, this.id || 'print-customizer', {
            source: 'print-customizer',
            itemCount: addedCount,
            productId: this.dataset.productId,
          })
        );
      } catch (refreshError) {
        console.error('[print-customizer] cart UI refresh failed', refreshError);
      }
    } catch (error) {
      console.error('[print-customizer] add to cart failed', error);
      this.#showError(error.message || 'Something went wrong. Please try again.');
    } finally {
      this.#setLoading(false);
    }
  };
}

if (!customElements.get('print-customizer')) {
  customElements.define('print-customizer', PrintCustomizer);
}
